import { PACED_GRACE_MS, isAnswerable, type Answer, type PacedState } from "@engage/contracts";
import { useEffect, useState } from "preact/hooks";
import { AnswerForm, Countdown, promptClass } from "./answer";
import { SessionClosed, pacedAnswer, pacedCurrent, pacedSkip, type Joined } from "./api";
import type { RuntimeConfig } from "./config";
import { useI18n } from "./i18n";
import { forget } from "./storage";

/**
 * A self-paced session (ADR 0008): one slide at a time, at this person's own pace.
 *
 * The server keeps their place and their clock. This screen only shows what it is
 * told and asks for the next slide, so a reload, a second tab or a phone that dies
 * halfway resumes exactly where they were — the same question, on the same timer.
 *
 * Nothing here says whether an answer was right or what it scored: in this mode only
 * the presenter sees the standings.
 */
type Screen =
  | { kind: "loading" }
  | { kind: "slide"; state: PacedState; offset: number; note?: "saved" | "late" }
  | { kind: "done"; state: PacedState }
  | { kind: "closed" }
  | { kind: "error" };

export function Paced({ config, joined, onLeave }: { config: RuntimeConfig; joined: Joined; onLeave: () => void }) {
  const { t, locale } = useI18n();
  const [screen, setScreen] = useState<Screen>({ kind: "loading" });

  const load = async (note?: "saved" | "late") => {
    try {
      const sent = Date.now();
      const state = await pacedCurrent(config, joined);
      // Server clock minus ours, at the midpoint of the round trip: the countdown runs
      // on the server's time, which is the time the answer is judged against.
      const offset = Date.parse(state.serverTime) - (sent + Date.now()) / 2;
      setScreen(state.current ? { kind: "slide", state, offset, ...(note ? { note } : {}) } : { kind: "done", state });
    } catch (err) {
      if (err instanceof SessionClosed) {
        forget(joined.code);
        setScreen({ kind: "closed" });
      } else {
        setScreen({ kind: "error" });
      }
    }
  };

  useEffect(() => {
    void load();
  }, []);

  if (screen.kind === "loading") {
    return (
      <section class="card stack center">
        <div class="dots" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      </section>
    );
  }

  if (screen.kind === "closed" || screen.kind === "error" || screen.kind === "done") {
    return (
      <section class="card stack center final-card">
        <h1>
          {screen.kind === "done" ? t("paced.done.title") : screen.kind === "closed" ? t("ended.gone") : t("join.error")}
        </h1>
        <p class="muted">
          {screen.kind === "done" ? t("paced.done.body") : screen.kind === "closed" ? t("paced.closed") : ""}
        </p>
        {/* The standings are the presenter's alone here; the code is how a winner proves it later. */}
        {screen.kind === "done" && joined.claimCode && (
          <div class="claim">
            <span>{t("final.claim")}</span>
            <strong class="claim-code" data-claim-code>
              {joined.claimCode}
            </strong>
            <small>{t("final.claim.hint")}</small>
          </div>
        )}
        {screen.kind === "error" ? (
          <button class="primary" onClick={() => void load()}>
            {t("paced.next")}
          </button>
        ) : (
          <button class="primary" onClick={onLeave}>
            {t("ended.again")}
          </button>
        )}
      </section>
    );
  }

  const { state, offset } = screen;
  const current = state.current!;
  const closes = state.closesAt
    ? new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(new Date(state.closesAt))
    : null;

  return (
    <>
      <header class="bar">
        <span class="who">{joined.displayName ?? ""}</span>
        <span class="bar-end">
          <span class="pill">{t("paced.progress", { n: current.index + 1, total: state.total })}</span>
        </span>
      </header>
      <div class="paced-track" aria-hidden="true">
        <span style={{ width: `${(100 * state.done) / Math.max(1, state.total)}%` }} />
      </div>
      <PacedSlide
        // Keyed by slide: each one is a new screen, with its own timer and form.
        key={current.slide.id}
        current={current}
        offset={offset}
        onAnswer={async (answer) => {
          const outcome = await pacedAnswer(config, joined, current.slide.id, answer).catch((err) => {
            if (err instanceof SessionClosed) setScreen({ kind: "closed" });
            throw err;
          });
          if (outcome === "saved" || outcome === "again") {
            setScreen({ ...screen, note: "saved" });
            setTimeout(() => void load(), 700);
            return null;
          }
          if (outcome === "late") {
            setScreen({ ...screen, note: "late" });
            return null;
          }
          return t("live.invalid", { reason: outcome.refused });
        }}
        onSkip={async () => {
          await pacedSkip(config, joined, current.slide.id).catch((err) => {
            if (err instanceof SessionClosed) setScreen({ kind: "closed" });
          });
          void load();
        }}
        onTimeUp={() => void load("late")}
        note={screen.note}
      />
      {closes && <p class="paced-closes muted">{t("paced.closes", { date: closes })}</p>}
    </>
  );
}

function PacedSlide({
  current,
  offset,
  note,
  onAnswer,
  onSkip,
  onTimeUp,
}: {
  current: NonNullable<PacedState["current"]>;
  offset: number;
  note?: "saved" | "late";
  onAnswer: (answer: Answer) => Promise<string | null>;
  onSkip: () => Promise<void>;
  onTimeUp: () => void;
}) {
  const { t } = useI18n();
  const { slide } = current;
  // The deadline on this phone's clock, corrected by the measured offset.
  const deadline = current.deadline ? Date.parse(current.deadline) - offset : null;
  const [late, setLate] = useState(() => deadline !== null && Date.now() > deadline);
  const [busy, setBusy] = useState(false);

  // When time runs out: say so, wait out the grace — inside it this question still
  // counts on the server, so asking for the next one sooner would return this one —
  // then move on.
  useEffect(() => {
    if (deadline === null || note === "saved") return;
    const untilDeadline = Math.max(0, deadline - Date.now());
    const shown = setTimeout(() => setLate(true), untilDeadline);
    const next = setTimeout(onTimeUp, untilDeadline + PACED_GRACE_MS + 250);
    return () => {
      clearTimeout(shown);
      clearTimeout(next);
    };
  }, [deadline, note]);

  if (!isAnswerable(slide.type)) {
    return (
      <section class="card stack center slide-only">
        <h1 class={promptClass(slide.prompt)}>{slide.prompt}</h1>
        <button
          class="primary"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void onSkip();
          }}
        >
          {t("paced.next")}
        </button>
      </section>
    );
  }

  const timeUp = note === "late" || late;
  return (
    <section class="stack asking">
      <div class="prompt-head">
        <h1 class={promptClass(slide.prompt)}>{slide.prompt}</h1>
        {!timeUp && note !== "saved" && <Countdown deadline={deadline} limitMs={slide.timeLimitMs} />}
      </div>

      {note === "saved" ? (
        <div class="card notice">
          <span class="tick" aria-hidden="true">
            ✓
          </span>
          <strong>{t("paced.saved")}</strong>
        </div>
      ) : timeUp ? (
        <div class="card notice">
          <strong>{t("paced.timeUp")}</strong>
          <p class="muted">{t("paced.timeUpBody")}</p>
        </div>
      ) : (
        <>
          <AnswerForm slide={slide} submit={onAnswer} />
          {current.skippable && (
            <button
              type="button"
              class="link"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void onSkip();
              }}
            >
              {t("paced.skip")}
            </button>
          )}
        </>
      )}
    </section>
  );
}
